// Look previews, shared by the Settings "Looks" grid and first-run setup (src/client/onboarding).
// lookScene(bundle, presets, fonts) draws a tiny start page in the look's real colors, font,
// corners and backdrop: tab strip, clock, search pill, shortcut dots and a primary button.
// Callers pass WALLPAPER_PRESET_MAP and the font registry, so the lazy setup bundle reuses the
// app's copies instead of shipping its own. safeUrl also guards the wallpaper layer
// (app/wallpaper.js).
import { inkFor, onAccent, contrast, mixHex } from "./color.js";

// A pasted wallpaper link, made safe to drop into url("..."): http(s) only, quotes and
// backslashes escaped, whitespace encoded. "" when it is not usable.
export function safeUrl(raw) {
	const v = String(raw || "").trim();
	if (!/^https?:\/\//i.test(v)) return "";
	return v.replace(/["\\]/g, encodeURIComponent).replace(/\s/g, "%20");
}

const isColor = (c) => typeof CSS !== "undefined" && CSS.supports && CSS.supports("color", c);

// A theme's tokens, read straight out of the loaded stylesheet, so theme.css stays the only
// copy of every palette. Custom properties only exist on :root, which a tile cannot inherit
// for another theme, hence reading the rules themselves. The base :root block fills in
// anything a theme leaves out, and simple var(--x) references are resolved.
const NAMES = ["bg", "surface", "surface-2", "line", "line-strong", "fg", "fg-2", "muted", "icon-tint", "on-accent", "accent-ink", "font-display", "look-backdrop", "sage", "zen-grain"];
const themeTokenCache = new Map();
function readRules(want) {
	const out = {};
	for (const sheet of document.styleSheets) {
		let rules = null;
		try { rules = sheet.cssRules; } catch { continue; } // a cross-origin sheet
		if (!rules) continue;
		for (const rule of rules) {
			const sel = rule.selectorText;
			if (!sel || !sel.split(",").some((x) => want.includes(x.trim()))) continue;
			for (const n of NAMES) { const v = rule.style.getPropertyValue("--" + n).trim(); if (v) out[n] = v; }
		}
	}
	return out;
}
export function themeTokens(theme) {
	const key = theme || "dark";
	if (themeTokenCache.has(key)) return themeTokenCache.get(key);
	let out = null;
	try {
		const base = readRules([":root"]);
		const own = key === "dark" ? {} : readRules([':root[data-theme="' + key + '"]', ':root[data-theme^="' + key.split("-")[0] + '"]']);
		if (base.bg || own.bg) out = Object.assign({}, base, own);
	} catch { /* a sheet went away mid-read: no preview colors */ }
	themeTokenCache.set(key, out);
	return out;
}

function resolve(t, v, extra, depth = 0) {
	const m = /^var\(--([\w-]+)\)$/.exec(v || "");
	if (!m || depth > 4) return v;
	const k = m[1];
	return resolve(t, k in extra ? extra[k] : t[k], extra, depth + 1);
}

// The CSS background for a bundle's wallpaper, or "" when it has none.
function wallpaperBg(b, presets) {
	const w = b.settings.wallpaper;
	const p = presets[w];
	if (p && p.kind === "gradient") return p.value;
	if (w === "solid" && isColor(b.settings.wallpaperSolid)) return b.settings.wallpaperSolid;
	if (w === "url") { const u = safeUrl(b.settings.wallpaperUrl); if (u) return `center/cover no-repeat url("${u}")`; }
	return "";
}
function thumbFor(b, presets) {
	const p = presets[b.settings.wallpaper];
	if (!p || p.kind !== "image") return null;
	if (/^\/wallpapers\/[\w-]+\.svg$/.test(p.value)) return { src: p.value, srcset: p.value }; // small enough to be its own thumbnail
	if (!/^\/wallpapers\/[\w-]+\.webp$/.test(p.value)) return null;
	const id = p.value.slice(12, -5);
	return { src: "/wallpapers/thumbs/" + id + ".webp", srcset: "/wallpapers/thumbs/" + id + ".webp 1x, /wallpapers/thumbs/" + id + "@2x.webp 2x" };
}

// Kept for anything that still wants a single background value for a look.
export function previewBg(b, presets) {
	const p = presets[b.settings.wallpaper];
	if (p && p.kind === "image") return `center/cover no-repeat url("${p.value}")`;
	const wb = wallpaperBg(b, presets);
	if (wb) return wb;
	const t = themeTokens(b.settings.theme);
	if (t) return t["look-backdrop"] && t["look-backdrop"] !== "none" ? t["look-backdrop"] + ", " + t.bg : t.bg;
	return b.settings.theme === "light" ? "#e7e7ea" : "#101012";
}

const RADII = { sharp: 2, rounded: 5, soft: 8 };
const el = (tag, cls) => { const e = document.createElement(tag); e.className = cls; return e; };

let styled = false;
function injectStyle() {
	if (styled) return;
	styled = true;
	const s = document.createElement("style");
	s.textContent = SCENE_CSS;
	document.head.append(s);
}

// fonts: { FONTS, loadFont } from shared/fonts.js. Fonts load when the card scrolls into view,
// so a long grid never pulls every web font at once.
let io = null;
function whenVisible(node, fn) {
	if (typeof IntersectionObserver === "undefined") { fn(); return; }
	if (!io) io = new IntersectionObserver((list) => { for (const e of list) if (e.isIntersecting) { io.unobserve(e.target); e.target._rvSeen && e.target._rvSeen(); } }, { rootMargin: "120px" });
	node._rvSeen = fn;
	io.observe(node);
}

export function lookScene(b, presets, fonts) {
	injectStyle();
	const s = b.settings;
	const t = themeTokens(s.theme) || {};
	const extra = { accent: s.accent || "#e9c46a" };
	const fontEntry = fonts && fonts.FONTS && fonts.FONTS[s.font];
	extra.font = fontEntry ? fontEntry.stack : "system-ui, sans-serif";
	const tok = {};
	for (const n of NAMES) tok[n] = resolve(t, t[n], extra);
	const bg = tok.bg || "#101012";
	// the default control border is a mix of the theme's text and panel colors
	if (!tok["line-strong"] || /var\(/.test(tok["line-strong"])) tok["line-strong"] = mixHex(tok.fg || "#eeeeee", tok.surface || bg, 0.45);
	const accent = extra.accent;
	const ink = inkFor(accent, [bg, tok.surface || bg, tok["surface-2"] || bg]);
	const ownDark = tok["on-accent"] && /^#[0-9a-f]{6}$/i.test(tok["on-accent"]) && contrast(tok["on-accent"], "#ffffff") > 10 ? tok["on-accent"] : "#1a1500";

	const scene = el("span", "rv-look");
	scene.setAttribute("aria-hidden", "true");
	const v = scene.style;
	v.setProperty("--t-bg", bg);
	v.setProperty("--t-surface", tok.surface || bg);
	v.setProperty("--t-surface-2", tok["surface-2"] || tok.surface || bg);
	v.setProperty("--t-line", tok.line || "rgba(127,127,127,.3)");
	v.setProperty("--t-line-strong", tok["line-strong"] || tok.line || "rgba(127,127,127,.5)");
	v.setProperty("--t-fg", tok.fg || "#eee");
	v.setProperty("--t-muted", tok.muted || "#999");
	v.setProperty("--t-accent", accent);
	v.setProperty("--t-ink", ink);
	v.setProperty("--t-icon", tok["icon-tint"] && !/var\(/.test(tok["icon-tint"]) ? tok["icon-tint"] : ink);
	v.setProperty("--t-on-accent", onAccent(accent, ownDark));
	v.setProperty("--t-r", (RADII[s.cornerStyle] ?? 5) + "px");

	// backdrop: wallpaper thumbnail, a gradient wallpaper, or the look's own skin backdrop
	const back = el("span", "rv-look-back");
	const thumb = thumbFor(b, presets);
	const wb = wallpaperBg(b, presets);
	if (thumb) {
		const img = document.createElement("img");
		img.alt = "";
		img.loading = "lazy";
		img.decoding = "async";
		img.width = 240;
		img.height = 150;
		img.src = thumb.src;
		img.srcset = thumb.srcset;
		back.append(img);
	} else if (wb) back.style.background = wb;
	else if (tok["look-backdrop"] && tok["look-backdrop"] !== "none") back.style.background = tok["look-backdrop"] + ", " + bg;
	if (thumb || wb) {
		const dim = Math.max(0, Math.min(85, Number(s.wallpaperDim ?? 35))) / 100;
		const scrim = el("span", "rv-look-scrim");
		scrim.style.opacity = String(Math.max(0.15, dim));
		back.append(scrim);
		scene.classList.add("has-wall");
	}
	scene.append(back);

	// a few looks carry a motif of their own
	if (/^zen/.test(s.theme) && !thumb && !wb) scene.append(el("span", "rv-look-enso"), el("span", "rv-look-seal"));

	const bar = el("span", "rv-look-bar");
	bar.append(el("span", "rv-look-tab"), el("span", "rv-look-omni"));
	// sample text lives in CSS content so it never joins the tile's name or text
	const clock = el("span", "rv-look-clock");
	clock.dataset.t = "9:41";
	const display = tok["font-display"] && !/^var\(/.test(tok["font-display"]) ? tok["font-display"] : extra.font;
	const search = el("span", "rv-look-search");
	const dots = el("span", "rv-look-dots");
	for (let i = 0; i < 4; i++) dots.append(el("span", "rv-look-dot"));
	const btn = el("span", "rv-look-btn");
	btn.dataset.t = "Go";
	scene.append(bar, clock, search, dots, btn);

	// Web fonts only once the card is near the screen; system fonts apply straight away.
	const applyFont = () => {
		if (fonts && fonts.loadFont) fonts.loadFont(s.font);
		if (fonts && fonts.loadDisplayFont) fonts.loadDisplayFont(s.theme);
		clock.style.fontFamily = display;
		btn.style.fontFamily = extra.font;
	};
	if (fontEntry && fontEntry.google) whenVisible(scene, applyFont); else applyFont();
	return scene;
}

const SCENE_CSS = `
.rv-look{position:relative;display:block;width:100%;aspect-ratio:16/10;overflow:hidden;container-type:inline-size;background:var(--t-bg);color:var(--t-fg);border-radius:inherit;isolation:isolate}
.rv-look-clock::before,.rv-look-btn::before{content:attr(data-t)}
.rv-look-back{position:absolute;inset:0;z-index:-1;background-size:cover;background-position:center}
.rv-look-back img{width:100%;height:100%;object-fit:cover;display:block}
.rv-look-scrim{position:absolute;inset:0;background:#000}
.rv-look-bar{position:absolute;left:0;right:0;top:0;height:13%;display:flex;align-items:center;gap:4%;padding:0 4%;background:var(--t-surface);border-bottom:1px solid var(--t-line)}
.has-wall .rv-look-bar{background:color-mix(in srgb,var(--t-surface) 82%,transparent)}
.rv-look-tab{width:22%;height:62%;border-radius:calc(var(--t-r)*.6) calc(var(--t-r)*.6) 0 0;background:var(--t-bg);align-self:flex-end;box-shadow:inset 0 2px 0 -1px var(--t-accent)}
.rv-look-omni{flex:1;height:50%;border-radius:var(--t-r);background:var(--t-surface-2)}
.rv-look-clock{position:absolute;left:0;right:0;top:22%;text-align:center;font-size:15cqw;line-height:1;letter-spacing:.02em;font-variant-numeric:lining-nums tabular-nums;color:var(--t-fg);font-family:system-ui,sans-serif}
.has-wall .rv-look-clock{top:24%;font-size:12cqw;text-shadow:0 1px 6px rgba(0,0,0,.5)}
.rv-look-search{position:absolute;left:20%;right:20%;top:52%;height:10%;border-radius:999px;background:var(--t-surface);border:1px solid var(--t-line-strong)}
.rv-look-dots{position:absolute;left:0;right:0;top:69%;display:flex;justify-content:center;gap:5cqw}
.rv-look-dot{width:7.5cqw;height:7.5cqw;border-radius:50%;background:var(--t-surface);border:1px solid var(--t-line);position:relative}
.rv-look-dot::after{content:"";position:absolute;inset:32%;border-radius:30%;background:var(--t-icon)}
.rv-look-btn{position:absolute;right:5%;bottom:7%;padding:.6cqw 3.6cqw;border-radius:var(--t-r);background:var(--t-accent);color:var(--t-on-accent);font:600 5.4cqw/1.5 system-ui,sans-serif}
.rv-look-enso{position:absolute;left:50%;top:13%;width:46cqw;height:46cqw;margin-left:-23cqw;background:var(--t-fg);opacity:.1;-webkit-mask:url(/looks/enso.svg) center/contain no-repeat;mask:url(/looks/enso.svg) center/contain no-repeat}
.rv-look-seal{position:absolute;left:66%;top:17%;width:3.4cqw;height:4.1cqw;background:var(--t-accent);-webkit-mask:url(/looks/seal.svg) center/contain no-repeat;mask:url(/looks/seal.svg) center/contain no-repeat;transform:rotate(-3.5deg);opacity:.85}
`;
